import { useEffect, useState } from 'react';
import { isM140, isM65 } from '~/features/products/components/DynamicCard/DynamicCard';
import { ProductListItem } from '~/features/products/model';
import { GA4EcommerceProduct, GA4ViewProductEvent, UaEcommerceProduct, UaImpressionViewEvent } from '~/features/tracking/model';
import { createGA4ProductTrackingObject, CreateGA4ProductTrackingObjectArgs } from '~/features/tracking/utils/createGA4ProductTrackingObject';
import { createProductTrackingObject } from '~/features/tracking/utils/createProductTrackingObject';
import { pushToDataLayer } from '~/features/tracking/utils/pushToDataLayer';
import { useFrame } from '~/shared/utils/frame/hooks/useFrame';

const chunkArr = <T extends unknown>(arr: T[], size: number): T[][] => {
	const chunks = [];

	for (let i = 0; i < arr.length; i = size + i) {
		chunks.push(arr.slice(i, i + size));
	}

	return chunks;
};

type UseProductViewTrackingOptions = {
	pageSize?: number;
	enable?: boolean;
	onTrack?: () => void;
	listId?: string;
	listName?: string;
};

const CHUNK_SIZE = 24;

export const useProductViewTracking = (list?: ProductListItem[], opts?: UseProductViewTrackingOptions) => {
	const [lastIndex, setLastIndex] = useState(0);
	const { data: frame } = useFrame();
	const currency = frame.market?.currency?.id || '';
	const { pageSize, enable } = opts || {};

	useEffect(() => {
		if (!enable || !list) {
			return;
		}

		const ga4products: GA4EcommerceProduct[] = [];
		const uaProducts: UaEcommerceProduct[] = [];

		// we iterate over the last items (size determined by page size) in the provided list
		for (const [index, item] of list.slice(pageSize ? -pageSize : 0).entries()) {
			if (index === list.length - 1) {
				// we store the index when the last item in the list has been reached.
				// the index is constructed by the previous last index + current index in the list + 1
				// (we add one to be able to handle zero indexes in next list).
				// we do this to be able to continue the index from the last list provided.
				setLastIndex((prevLastIndex) => prevLastIndex + index + 1);
			}

			if (isM65(item) || isM140(item)) {
				continue;
			}

			uaProducts.push(
				createProductTrackingObject(item, {
					position: index,
					listName: opts?.listName,
				}),
			);

			const ga4ProductArgs: CreateGA4ProductTrackingObjectArgs = {
				variation: item,
				currency: currency.toUpperCase(),
				index: lastIndex + index,
			};

			if (opts?.listId) {
				ga4ProductArgs.itemListId = opts.listId.toLowerCase();
			}

			if (opts?.listName) {
				ga4ProductArgs.itemListName = opts.listName.toLowerCase();
			}

			ga4products.push(createGA4ProductTrackingObject(ga4ProductArgs));
		}

		for (const chunk of chunkArr<UaEcommerceProduct>(uaProducts, CHUNK_SIZE)) {
			pushToDataLayer<UaImpressionViewEvent>({
				event: 'eec_impressionView',
				ecommerce: {
					currencyCode: currency.toUpperCase(),
					impressions: chunk,
				},
			});
		}

		for (const chunk of chunkArr<GA4EcommerceProduct>(ga4products, CHUNK_SIZE)) {
			pushToDataLayer<GA4ViewProductEvent>({
				event: 'view_item_list',
				ecommerce: {
					items: chunk,
				},
			});
		}

		if (opts?.onTrack) {
			opts.onTrack();
		}
	}, [list, enable]);
};
